import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Textarea from '../ui/Textarea';
import { cn } from '../../lib/utils';

export type ConfirmTone = 'default' | 'warn' | 'danger';

interface ConfirmActionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** Show a textarea and require at least `minReasonLength` characters. */
  requireReason?: boolean;
  /** Show the textarea but allow it to be empty. */
  optionalReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  initialReason?: string;
  minReasonLength?: number;
  isPending?: boolean;
  error?: string | null;
  /** Extra controls rendered between the description and the reason field. */
  children?: ReactNode;
  onConfirm: (reason: string) => void | Promise<void>;
}

export default function ConfirmActionDialog({
  isOpen,
  onClose,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  requireReason = false,
  optionalReason = false,
  reasonLabel = 'Reason',
  reasonPlaceholder = 'Shown to the affected user and stored in the audit log',
  initialReason = '',
  minReasonLength = 3,
  isPending = false,
  error,
  children,
  onConfirm,
}: ConfirmActionDialogProps) {
  const [reason, setReason] = useState(initialReason);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason(initialReason);
      setTouched(false);
    }
  }, [isOpen, initialReason]);

  const showReason = requireReason || optionalReason;
  const trimmed = reason.trim();
  const reasonTooShort = requireReason && trimmed.length < minReasonLength;
  const canConfirm = !isPending && !reasonTooShort;

  const submit = () => {
    setTouched(true);
    if (!canConfirm) return;
    void onConfirm(trimmed);
  };

  const confirmVariant = tone === 'danger' ? 'destructive' : 'default';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isPending) onClose();
      }}
      title={title}
      size="sm"
      mobilePresentation="sheet"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={submit}
            disabled={!canConfirm}
            className={cn(tone === 'warn' && 'bg-[rgb(var(--warn))] border-[rgb(var(--warn))] text-white hover:brightness-110')}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {tone !== 'default' && (
          <div
            className={cn(
              'flex items-start gap-2 rounded-lg border p-3 text-sm',
              tone === 'danger'
                ? 'border-[rgb(var(--danger)/0.4)] bg-[rgb(var(--danger)/0.1)] text-[rgb(var(--danger))]'
                : 'border-[rgb(var(--warn)/0.4)] bg-[rgb(var(--warn)/0.1)] text-[rgb(var(--warn))]'
            )}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="text-glass-text">{description ?? 'This action affects a live account.'}</div>
          </div>
        )}
        {tone === 'default' && description && <div className="text-sm text-glass-text-2">{description}</div>}

        {children}

        {showReason && (
          <div>
            <label className="mb-1 block text-sm font-medium text-glass-text">
              {reasonLabel}
              {requireReason && <span className="text-[rgb(var(--danger))]"> *</span>}
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder={reasonPlaceholder}
              rows={3}
              disabled={isPending}
              autoFocus
            />
            {touched && reasonTooShort && (
              <p className="mt-1 text-xs text-[rgb(var(--danger))]">
                Please give a reason (at least {minReasonLength} characters).
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">{error}</div>
        )}
      </div>
    </Modal>
  );
}
